import axiosClient from "utils/axios";
import { ApiList, ApiOptionList, ApiResponse } from "types/api.types";
import {
  ClientAdvance,
  CreateClientAdvanceRequest,
  ReceiveClientAdvanceRequest,
  RefundClientAdvanceRequest,
  UpdateClientAdvanceRequest,
} from "features/ClientAdvances/types/client-advance.types";
import type {
  AdvanceSummaryData,
  AdvanceSummaryParams,
} from "components/common/AdvanceSummaryWidgets";

export const getClientAdvances = async ({
  page = 1,
  limit = 10,
  search,
  status,
  sourceType,
  sourceId,
}: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  sourceType?: string;
  sourceId?: number;
} = {}) => {
  try {
    const response = await axiosClient.post<ApiResponse<ApiList<ClientAdvance[]>>>(
      "/client-advances/all",
      {
        page,
        limit,
        search: search || undefined,
        status: status || undefined,
        sourceType: sourceType || undefined,
        sourceId: sourceId || undefined,
      },
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Client Advance list : ", err);
    throw err;
  }
};

/** GET /client-advances/summary — mirrors GET /payments/summary's query contract. */
export const getClientAdvanceSummary = async (params: AdvanceSummaryParams) => {
  try {
    const response = await axiosClient.get<ApiResponse<AdvanceSummaryData>>(
      "/client-advances/summary",
      { params },
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Client Advance summary : ", err);
    throw err;
  }
};

export const getClientAdvanceOptions = async ({
  query,
  limit,
  page,
  status,
  ids,
  withCancelledDisbursement,
}: {
  query?: string;
  limit?: number;
  page?: number;
  status?: string;
  ids?: string;
  withCancelledDisbursement?: boolean;
}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiOptionList>>(
      `/client-advances/options`,
      {
        params: { query, limit, page, status, ids, withCancelledDisbursement },
      },
    );

    return response.data;
  } catch (err) {
    console.warn("Error fetching Client Advance Options : ", err);
    throw err;
  }
};

export const createClientAdvance = async (payload: CreateClientAdvanceRequest) => {
  try {
    const response = await axiosClient.post<ApiResponse<ClientAdvance>>(
      "/client-advances",
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error creating Client Advance : ", err);
    throw err;
  }
};

export const updateClientAdvance = async (payload: UpdateClientAdvanceRequest) => {
  try {
    const response = await axiosClient.put<ApiResponse<ClientAdvance>>(
      `/client-advances`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error updating Client Advance : ", err);
    throw err;
  }
};

export const getClientAdvance = async (id: number) => {
  try {
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error("Invalid ID");
    }
    const response = await axiosClient.post<ApiResponse<ClientAdvance>>(
      `/client-advances/detail`,
      { id },
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Client Advance details : ", err);
    throw err;
  }
};

export const deleteClientAdvance = async (id: number) => {
  try {
    const response = await axiosClient.delete<ApiResponse<ClientAdvance>>(
      `/client-advances`,
      { data: { id } },
    );
    return response.data;
  } catch (err) {
    console.warn("Error deleting Client Advance : ", err);
    throw err;
  }
};

export const cancelClientAdvance = async (id: number, reason?: string | null) => {
  try {
    const response = await axiosClient.post<ApiResponse<ClientAdvance>>(
      `/client-advances/${id}/cancel`,
      reason?.trim() ? { reason: reason.trim() } : {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error cancelling Client Advance : ", err);
    throw err;
  }
};

/** DRAFT → PROCESSED — attaches the Payment. */
export const receiveClientAdvance = async (
  id: number,
  payload: ReceiveClientAdvanceRequest,
) => {
  try {
    const response = await axiosClient.post<ApiResponse<ClientAdvance>>(
      `/client-advances/${id}/receive`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error receiving Client Advance : ", err);
    throw err;
  }
};

/** PROCESSED → REFUNDED — refunds the full remaining (un-settled) balance only. */
export const refundClientAdvance = async (
  id: number,
  payload: RefundClientAdvanceRequest,
) => {
  try {
    const response = await axiosClient.post<ApiResponse<ClientAdvance>>(
      `/client-advances/${id}/refund`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error refunding Client Advance : ", err);
    throw err;
  }
};
